import moment from 'moment';
import qs from 'querystring';
import { useDispatch } from 'react-redux';

import { useCallback, useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useParams, useLocation, Redirect, useHistory } from 'react-router-dom';

import * as api from '../../../api';
import * as rootReducer from '../../../reducers/root';

import introPageStyles from '../../FormStart/IntroPage/index.module.css';
import formStyles from '../../FormStart/index.module.css';
import styles from '../index.module.css';

import Disclaimer from '../Disclaimer';
import EclipseLoader from '../../../components/EclipseLoader';
import Footer from '../Footer';
import Loader from '../../../components/Loader';
import NavBar, { View } from '../../../components/NavBar';
import NoCookies from '../NoCookies';
import RegistrationModal from './Modal';
import RegistrationForm, { FormModel } from './RegistrationForm';
import { Option } from '../../../components/utils';
import { parseValue } from 'common/dist/utils/types';
import { useTheme } from '../../../hooks/common/useTheme';
import { verifyRequest } from '../../../utils';

export enum Page {
  Booking = 'BOOKING',
  Login = 'LOGIN',
  Loading = 'LOADING',
  Ineligible = 'INELIGIBLE',
  Success = 'SUCCESS',
}

export const isRegistrationContactVerification = (
  registered:
    | {
        authenticationCode: string;
      }
    | api.Register.RegistrationContactVerification,
): registered is api.Register.RegistrationContactVerification => {
  return 'patient' in registered;
};

export const getContactVerificationFields = (
  allowedVerifications: api.Register.AllowedVerification[],
): Option<api.Register.AllowedVerification>[] => {
  return allowedVerifications.map((verification) => ({
    key: verification.kind,
    value: verification,
    display: verification.display,
  }));
};

export const getErrorPageByStatusCode = async (
  start: Date,
  t: TFunction,
  status?: number,
): Promise<Page> => {
  const timeElapsed = new Date().getTime() - start.getTime();
  if (timeElapsed <= MINIMUM_LOAD_TIME) {
    await new Promise((res) =>
      setTimeout(res, MINIMUM_LOAD_TIME - timeElapsed),
    );
  }

  if (!status || status >= 500) {
    alert(t('somethingWentWrongRefreshOrContactSupport'));
    return Page.Login;
  }
  return Page.Ineligible;
};

const getMaybeProvisionalAuthCodeFromSearch = (
  search: string,
): string | undefined => {
  const queryValues = qs.parse(search.slice(1));
  const { code } = queryValues;
  return Array.isArray(code) ? code[0] : code;
};

export const MINIMUM_LOAD_TIME = 500; // ms

const Registration: React.FC = () => {
  const { t } = useTranslation(['login']);
  const { registrationCode } = useParams<{ registrationCode: string }>();
  const { pathname: pathName, search } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    isThemeLoading,
    theme: { additionalLoginInputs: themeAdditionalInput, introMessage },
  } = useTheme({
    registrationCode,
    provisionalAuthCode: getMaybeProvisionalAuthCodeFromSearch(search),
  });

  const [additionalLoginInputs, setAdditionalLoginInputs] = useState<
    api.PracticePreference.PatientLoginInput[]
  >([]);
  const [provisionalPatient, setProvisionalPatient] =
    useState<api.ProvisionalPatient.ProvisionalPatient | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authCode, setAuthCode] = useState<string>();
  const [page, setPage] = useState(Page.Login);
  const [verificationOptions, setVerificationOptions] = useState<
    Option<api.Register.AllowedVerification>[]
  >([]);
  const [formValues, setFormValues] = useState<FormModel | undefined>();

  const isUrgentCareWalkIn = !!additionalLoginInputs.length;

  const setOnCaptchaSuccess = useCallback(
    (onCaptchaSuccess: (() => void) | null) => {
      dispatch(rootReducer.setOnCaptchaSuccess(onCaptchaSuccess));
    },
    [dispatch],
  );

  useEffect(() => {
    const queryValues = qs.parse(search.slice(1));
    const source = queryValues && `${queryValues.source}`;
    const createLog = async () => {
      await api.client.Previsit.createPrevisitLog({
        auditLog:
          api.Log.PrevisitAuditAction
            .PatientPrevisitNavigatedToRegistrationPage,
        previsitLog: api.Log.PrevisitAction.NavigatedToRegistrationPage,
        source,
        pathName,
      });
    };
    createLog();
  }, [pathName, search]);

  useEffect(() => {
    if (!themeAdditionalInput) return;
    setAdditionalLoginInputs(themeAdditionalInput);
  }, [themeAdditionalInput]);

  if (isThemeLoading) {
    return <Loader />;
  }

  if (page === Page.Loading) {
    return (
      <div className={formStyles.Form}>
        <NavBar view={View.Login} />
        <EclipseLoader message={t('Loading...')} />
      </div>
    );
  }

  if (page === Page.Ineligible) {
    // TODO: Make this a practice pref + use code to figure out practice?
    const ineligibleMessage = isUrgentCareWalkIn
      ? t(
          'We are unable to register you at the moment, please see the front desk.',
        )
      : `
  ${t(
    'Based on the information you entered, you are not yet eligible to receive a vaccine.',
  )}

  ${t(
    "If you believe you've received this message in error, please try again or call us at {{phoneNumber}}",
    {
      phoneNumber: '(816) 691-2829',
    },
  )}`;
    return (
      <div className={formStyles.Form}>
        <NavBar view={View.Login} />
        <div className={styles.Flex}>
          <div className={introPageStyles.Label}>{t(ineligibleMessage)}</div>
        </div>
      </div>
    );
  }

  if (page === Page.Success && authCode) {
    // Redirect to normal page flow
    return <Redirect to={`/form/${authCode}/start`} />;
  }
  const message = constructIntroMessage(isUrgentCareWalkIn, t, introMessage);

  const handleSubmit = async (values: FormModel) => {
    const { firstName, lastName, dateOfBirth, cellPhoneNumber, email, sex } =
      values;
    if (!moment(dateOfBirth).isValid()) {
      alert(
        t('Invalid date of birth format: {{dateOfBirth}}', {
          dateOfBirth,
        }),
      );
      return;
    }
    setFormValues(values);
    const { hostname } = window.location;
    const domain = hostname;
    const { code } = qs.parse(search.slice(1));

    setIsSubmitting(true);
    setPage(Page.Loading);

    const payload = {
      registrationCode,
      domain,
      firstName,
      lastName,
      dateOfBirth,
      patientCode: Array.isArray(code) ? code[0] : code,
      ...{ cellPhoneNumber },
      ...{ email },
      ...{ sex },
    };
    const start = new Date();

    try {
      await verifyRequest(
        () => api.client.Previsit.register(payload),
        async (
          registered:
            | {
                authenticationCode: string;
              }
            | api.Register.RegistrationContactVerification,
        ) => {
          if (isUrgentCareWalkIn) {
            'patient' in registered &&
              setProvisionalPatient(registered.patient);

            if ('contactVerification' in registered) {
              const verifications = getContactVerificationFields(
                registered.allowedVerifications,
              );
              setVerificationOptions(verifications);
              setIsModalOpen(true);
            }
          } else if ('authenticationCode' in registered) {
            setAuthCode(registered.authenticationCode);
          }
        },
        setOnCaptchaSuccess,
      );
    } catch (err) {
      const registerError = parseValue<{ status: number }>(err.message);
      const page = await getErrorPageByStatusCode(
        start,
        t,
        registerError?.status,
      );
      setPage(page);
      return;
    } finally {
      setIsSubmitting(false);
    }
    const timeElapsed = new Date().getTime() - start.getTime();
    if (timeElapsed <= MINIMUM_LOAD_TIME) {
      await new Promise((res) =>
        setTimeout(res, MINIMUM_LOAD_TIME - timeElapsed),
      );
    }
    setIsSubmitting(false);
    setPage(Page.Success);
  };

  return (
    <div className={styles.LoginWrapper}>
      <NavBar view={View.Login} />
      <div className={styles.Login}>
        <NoCookies />
        <div className={styles.Instructions}>{message}</div>
        <RegistrationForm
          formValues={formValues}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          additionalLoginInputs={additionalLoginInputs}
        />
        <Disclaimer />
      </div>
      <Footer />
      <RegistrationModal
        isModalOpen={isModalOpen}
        provisionalPatientId={
          (provisionalPatient && provisionalPatient.id) || null
        }
        setIsModalOpen={setIsModalOpen}
        verificationOptions={verificationOptions}
        onPatientVerifiedAndLoggedIn={async ({
          authenticationCode,
        }: {
          authenticationCode: string;
        }) => {
          history.push(`/form/${authenticationCode}/start`);
        }}
      />
    </div>
  );
};

const constructIntroMessage = (
  isUrgentCareWalkIn: boolean,
  t: TFunction,
  introMessage?: string | null,
) => {
  if (introMessage) return introMessage;
  if (isUrgentCareWalkIn) return t('Please provide your information:');
  return t(
    'Please enter the following information to confirm your vaccine status:',
  );
};

export default Registration;
